import {
  FormNameEnum,
  OnboardingStatus,
  OnboardingStepType,
  OrganizationDetailFragment,
  useStartOnboardingStep,
} from '@alice-financial/alice-sdk'
import { Box, ExternalLink, Grid, Select, Typography } from '@alice-financial/pretext-ui'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import * as React from 'react'
import { parseFormInputs } from '../../survey/formValuesUtils'
import { fieldAnswer } from '../../survey/questionTypes/formFieldUtils'
import { Question } from '../../survey/types'
import { CompleteOnboardingStepButton } from '../onboarding/CompleteOnboardingStepButton'
import { CreateFormResponseVariables } from '../survey/useCreateFormResponse'
import { useCreateOrgFormResponse } from '../survey/useCreateOrgFormResponse'
import { useOrgFormFields } from '../survey/useOrgFormFields'
import { getShiftsStep } from './useShiftsStatus'

const SHIFT_SCHEDULING_FIELD = 'shiftSchedulingSystem'
const SHIFT_SCHEDULING_OPTIONS = [
  'None',
  '7Shifts',
  'Deputy',
  'Sling by Toast',
  'ZoomShift',
  'Homebase',
  'Connecteam',
  'HotSchedules',
  'Dayforce',
  'Harri',
  'Other',
] as const
const SHIFT_SCHEDULING_QUESTION_TEXT = 'Select your shift scheduling system'

type ShiftSchedulingOption = (typeof SHIFT_SCHEDULING_OPTIONS)[number]

// Create a question object for form parsing
const SHIFT_SCHEDULING_QUESTION: Question = {
  questionText: SHIFT_SCHEDULING_QUESTION_TEXT,
  questionId: SHIFT_SCHEDULING_FIELD,
  required: false,
  getFieldValue: (answer) => answer || '',
  getAnswer: (values: Record<string, string>) => values[SHIFT_SCHEDULING_FIELD] || '',
}

const SHIFT_SCHEDULING_HELP_LINKS: Record<string, string> = {
  '7Shifts': 'https://help.thisisalice.com/article/256-connecting-7shifts-and-alice',
  Deputy: 'https://help.thisisalice.com/article/254-connecting-deputy-and-alice',
  Other: 'https://help.thisisalice.com/article/258-connecting-shift-platforms-and-alice-general-instructions',
}

const ShiftSchedulingHelpText = ({ value }: { value: ShiftSchedulingOption | undefined }) => {
  if (!value || value === 'None') return null
  const href = SHIFT_SCHEDULING_HELP_LINKS[value] || SHIFT_SCHEDULING_HELP_LINKS.Other
  if (!href) return null

  return (
    <>
      <Typography variant="body2" gutterBottom>
        Follow{' '}
        <ExternalLink href={href} target="_blank">
          these instructions to connect {value === 'Other' ? 'your shift scheduling system' : value} with
          Alice <OpenInNewIcon fontSize="small" />
        </ExternalLink>
      </Typography>
    </>
  )
}

const SHIFT_SCHEDULING_QUESTIONS: Question[] = [SHIFT_SCHEDULING_QUESTION]

const SHIFT_FORM_NAME = FormNameEnum.ShiftScheduling
const SHIFT_FORM_VERSION = 1

export const useSelectedShiftSystem = (org: OrganizationDetailFragment) => {
  const formFields = useOrgFormFields(org.id, SHIFT_FORM_NAME, SHIFT_FORM_VERSION)
  return fieldAnswer<ShiftSchedulingOption>(formFields, SHIFT_SCHEDULING_QUESTION.questionId)
}

export const ShiftSurvey = ({ org }: { org: OrganizationDetailFragment }) => {
  const { mutateAsync: createFormResponse, isLoading } = useCreateOrgFormResponse({
    formName: SHIFT_FORM_NAME,
    formVersion: SHIFT_FORM_VERSION,
  })
  const { mutate: startOnboardingStep } = useStartOnboardingStep(org.id)

  const selectedSystem = useSelectedShiftSystem(org) || 'None'

  const handleSelectChange = (newValue: string) => {
    // Start CONNECT_SHIFTS step when shift system is selected OR confirmed to be 'None'
    startOnboardingStep({ stepType: OnboardingStepType.ConnectShifts })

    // Create form data with the new value
    const formData = { [SHIFT_SCHEDULING_FIELD]: newValue }
    const parsedData = parseFormInputs(SHIFT_SCHEDULING_QUESTIONS, formData)

    // Save immediately
    createFormResponse(parsedData as CreateFormResponseVariables)
  }

  if (!org) return null

  const step = getShiftsStep(org.onboardingWorkflow)
  const isNoneSelected = selectedSystem === 'None'

  return (
    <>
      <Typography variant="body2" gutterBottom maxWidth="sm">
        Connecting Alice to your scheduling system helps us process benefits in the right location for
        employees and quickly see when employees are not working or are terminated.
      </Typography>
      <Grid container spacing={3} flexDirection="column" p={2}>
        <Grid container flexWrap="nowrap" borderBottom={1} borderColor="divider">
          <Typography variant="h1" component="p" fontSize="3.5rem" fontWeight="bolder">
            1
          </Typography>
          <Grid container spacing={0} pb={2}>
            <Typography variant="h3" color="text.secondary" gutterBottom>
              {SHIFT_SCHEDULING_QUESTION_TEXT}
            </Typography>
            <Grid container alignItems="center" width="100%" spacing={2}>
              <Select
                variant="outlined"
                value={selectedSystem}
                disabled={isLoading}
                onChange={(e) => handleSelectChange(e.target.value as string)}
              >
                <option value={undefined} disabled>
                  Select an option
                </option>
                {SHIFT_SCHEDULING_OPTIONS.map((label, index) => (
                  <option key={index} value={label}>
                    {label}
                  </option>
                ))}
              </Select>
              {isNoneSelected && org.onboardingWorkflow && step?.status !== OnboardingStatus.Completed && (
                <CompleteOnboardingStepButton
                  orgId={org.id}
                  stepType={OnboardingStepType.ConnectShifts}
                  dataChange="shift_scheduling_connected"
                  label="Confirm"
                  onClick={() => {
                    const data = { [SHIFT_SCHEDULING_FIELD]: selectedSystem }
                    const parsedData = parseFormInputs(SHIFT_SCHEDULING_QUESTIONS, data)
                    createFormResponse(parsedData as CreateFormResponseVariables)
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid container flexWrap="nowrap">
          <Typography
            variant="h1"
            color={isNoneSelected ? 'action.disabled' : 'primary'}
            component="p"
            fontSize="3.5rem"
            fontWeight="bolder"
          >
            2
          </Typography>
          <Grid pb={2}>
            <Typography
              variant="h3"
              color={isNoneSelected ? 'action.disabled' : 'text.secondary'}
              gutterBottom
            >
              Grant access to view shift assignments
            </Typography>
            {isNoneSelected ? (
              <Typography variant="body2" gutterBottom color="text.disabled">
                Please select a shift scheduling system to continue.
              </Typography>
            ) : (
              <Box>
                <Typography variant="body2" fontWeight="bold" gutterBottom></Typography>
                <ShiftSchedulingHelpText value={selectedSystem} />
                {org.onboardingWorkflow && step?.status !== OnboardingStatus.Completed && (
                  <CompleteOnboardingStepButton
                    orgId={org.id}
                    stepType={OnboardingStepType.ConnectShifts}
                    dataChange="shift_scheduling_connected"
                    label="Instructions completed"
                  />
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}
